<template>
    <v-dialog
        v-model="dialog"
        max-width="1400px"
        scrollable
        persistent
        @click:outside="close"
    >
        <v-card :loading="loading">
            <v-card-title>
                <v-btn
                    small
                    icon
                    @click="close"
                    color="primary"
                    class="ml-n3 mr-2"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <h2 class="font-weight-regular">
                    Make Changes - {{ alertTypeLabel }}
                </h2>
            </v-card-title>
            <v-divider class="ml-4 mr-5" />
            <v-card-text>
                <div v-if="alertType === 'add'">
                    <v-alert dense outlined type="info" class="mb-2 mt-2">
                        Maximum {{ MAX_WORK_ORDER_ITEMS }} items per work order.
                    </v-alert>
                    <v-chip
                        color="primary"
                        class="mb-0 mt-2"
                        style="width: 100%; font-size: 14px; text-align: center; justify-content: center;"
                        v-if="workOrder.assemblyWorkOrder"
                    >
                        For this alert, only the main item can be added. Once
                        the change is accepted, an additional alert will be
                        automatically created to add the components.
                    </v-chip>
                    <v-data-table
                        :headers="addedItemsHeaders"
                        :items="addedItems"
                        class="elevation-0 mb-4 mt-2"
                        :mobile-breakpoint="0"
                    >
                        <template v-slot:[`item.partNumber`]="{ item }">
                            {{ item.partNumber }}
                        </template>
                        <template v-slot:[`item.description`]="{ item }">
                            {{ item.description }}
                        </template>
                        <template v-slot:[`item.quantity`]="{ item }">
                            {{ item.quantity }}
                        </template>
                        <template v-slot:[`item.files`]="{ item }">
                            <v-chip
                                v-if="item.files && item.files.length > 0"
                                small
                            >
                                {{ item.files.length }} file(s)
                            </v-chip>
                            <span v-else class="grey--text">-</span>
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                            <v-btn small icon @click="editAddedItem(item)">
                                <v-icon small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn
                                small
                                icon
                                color="error"
                                @click="removeAddedItem(item)"
                            >
                                <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                    <v-btn color="primary" rounded @click="openAddItemDialog">
                        <v-icon left small>mdi-plus</v-icon>
                        ADD ITEM
                    </v-btn>
                </div>

                <div v-else-if="alertType === 'remove'">
                    <v-alert type="info" class="mb-4">
                        <strong>Remove Part Numbers</strong>
                        <p class="mt-2">
                            The following part numbers will be removed from the
                            work order:
                        </p>
                        <v-chip
                            v-for="pn in selectedPartNumbersSorted"
                            :key="pn.id"
                            class="mr-2 mb-2"
                        >
                            {{ pn.partNumber }}
                        </v-chip>
                    </v-alert>
                    <p class="text-center">
                        Please confirm if you want to proceed with the removal.
                    </p>
                </div>

                <div v-else-if="alertType === 'edit'">
                    <v-tabs
                        v-model="currentEditTab"
                        v-if="selectedPartNumbersSorted.length > 1"
                    >
                        <v-tab
                            v-for="pn in selectedPartNumbersSorted"
                            :key="pn.id"
                        >
                            {{ pn.partNumber }}
                        </v-tab>
                    </v-tabs>
                    <v-tabs-items
                        v-model="currentEditTab"
                        v-if="selectedPartNumbersSorted.length > 1"
                    >
                        <v-tab-item
                            v-for="pn in selectedPartNumbersSorted"
                            :key="pn.id"
                        >
                            <div v-if="editItems[pn.id]" class="pt-4">
                                <ItemForm
                                    :ref="`itemForm-${pn.id}`"
                                    :workOrder="workOrder"
                                    :partNumbers="partNumbers"
                                    :item="editItems[pn.id].current"
                                    :originalItem="editItems[pn.id].original"
                                    :createForm="false"
                                    :settingProcesses="settingProcesses"
                                    :settingFinish="settingFinish"
                                    :OCs="OCs"
                                    :positions="positions"
                                    :alertMode="true"
                                    @replaceItem="
                                        item => handleItemUpdated(pn.id, item)
                                    "
                                    @files-changed="
                                        files => syncEditItemFiles(pn.id, files)
                                    "
                                    @updateProcesses="
                                        processes =>
                                            updateProcesses(pn.id, processes)
                                    "
                                    @close="() => {}"
                                />
                            </div>
                        </v-tab-item>
                    </v-tabs-items>
                    <div
                        v-else-if="
                            selectedPartNumbersSorted.length === 1 &&
                                editItems[selectedPartNumbersSorted[0].id]
                        "
                    >
                        <ItemForm
                            :ref="`itemForm-${selectedPartNumbersSorted[0].id}`"
                            :workOrder="workOrder"
                            :partNumbers="partNumbers"
                            :item="
                                editItems[selectedPartNumbersSorted[0].id]
                                    .current
                            "
                            :originalItem="
                                editItems[selectedPartNumbersSorted[0].id]
                                    .original
                            "
                            :createForm="false"
                            :settingProcesses="settingProcesses"
                            :settingFinish="settingFinish"
                            :OCs="OCs"
                            :positions="positions"
                            :alertMode="true"
                            @replaceItem="
                                item =>
                                    handleItemUpdated(
                                        selectedPartNumbersSorted[0].id,
                                        item
                                    )
                            "
                            @files-changed="
                                files =>
                                    syncEditItemFiles(
                                        selectedPartNumbersSorted[0].id,
                                        files
                                    )
                            "
                            @updateProcesses="
                                processes =>
                                    updateProcesses(
                                        selectedPartNumbersSorted[0].id,
                                        processes
                                    )
                            "
                        />
                    </div>
                    <div v-else class="text-center pa-8">
                        <v-progress-circular
                            indeterminate
                            color="primary"
                        ></v-progress-circular>
                        <p class="mt-4">Loading...</p>
                    </div>
                </div>

                <div v-else-if="alertType === 'editAssembly'">
                    <v-tabs
                        v-model="currentAssemblyTab"
                        v-if="selectedPartNumbersSorted.length > 1"
                    >
                        <v-tab
                            v-for="pn in selectedPartNumbersSorted"
                            :key="pn.id"
                        >
                            {{ pn.partNumber }}
                        </v-tab>
                    </v-tabs>
                    <v-tabs-items
                        v-model="currentAssemblyTab"
                        v-if="selectedPartNumbersSorted.length > 1"
                    >
                        <v-tab-item
                            v-for="pn in selectedPartNumbersSorted"
                            :key="pn.id"
                        >
                            <div
                                v-if="assemblyTabParents[pn.id]?.parentForForm"
                                class="pt-4"
                            >
                                <AssemblyForm
                                    :ref="'assemblyForm-' + pn.id"
                                    :key="'asm-' + pn.id"
                                    :workOrder="workOrder"
                                    :parentItem="
                                        assemblyTabParents[pn.id].parentForForm
                                    "
                                    :originalItems="assemblyCatalogItems"
                                    :alertMode="true"
                                    :assemblyTableReadOnly="
                                        partNumberLineHasProduction(pn.id)
                                    "
                                />
                            </div>
                        </v-tab-item>
                    </v-tabs-items>
                    <div
                        v-else-if="
                            selectedPartNumbersSorted.length === 1 &&
                                assemblyTabParents[
                                    selectedPartNumbersSorted[0].id
                                ]?.parentForForm &&
                                assemblyCatalogItems.length > 0
                        "
                    >
                        <AssemblyForm
                            :ref="
                                'assemblyForm-' +
                                    selectedPartNumbersSorted[0].id
                            "
                            :key="'asm-' + selectedPartNumbersSorted[0].id"
                            :workOrder="workOrder"
                            :parentItem="
                                assemblyTabParents[
                                    selectedPartNumbersSorted[0].id
                                ].parentForForm
                            "
                            :originalItems="assemblyCatalogItems"
                            :alertMode="true"
                            :assemblyTableReadOnly="
                                partNumberLineHasProduction(
                                    selectedPartNumbersSorted[0].id
                                )
                            "
                        />
                    </div>
                    <div
                        v-else
                        class="text-center pa-10"
                        style="min-height: 300px"
                    >
                        <v-progress-circular
                            indeterminate
                            color="primary"
                        ></v-progress-circular>
                        <p class="mt-4">Loading...</p>
                    </div>
                </div>

                <!-- Work order attachments (all alert types) -->
                <v-divider class="my-4" />
                <h4 class="mb-3">Work Order Attachments</h4>
                <p class="caption grey--text mb-2">
                    Add or remove files from the work order attachments.
                    <template v-if="isMajorModification">
                        They will be applied to the work order only when the
                        supervisor approves this alert.
                    </template>
                    <template v-else>
                        They are stored in the work order and applied when you
                        submit.
                    </template>
                </p>
                <div class="d-flex align-center flex-wrap mb-2">
                    <v-btn
                        v-for="(file, i) in editableAttachments"
                        :key="i"
                        color="grey lighten-3"
                        elevation="0"
                        x-small
                        class="ma-1 text-capitalize grey--text text--darken-1"
                    >
                        <span
                            class="file-link"
                            style="cursor: pointer;"
                            @click="openAttachment(file)"
                        >
                            {{ file }}
                        </span>
                        <v-icon
                            class="ml-1"
                            small
                            @click="removeAttachment(i)"
                            :disabled="loading"
                        >
                            mdi-close-circle
                        </v-icon>
                    </v-btn>
                    <v-btn
                        depressed
                        fab
                        x-small
                        color="primary"
                        class="ma-1"
                        @click="$refs.attachmentInput.click()"
                        :disabled="loading"
                    >
                        <v-icon small>mdi-plus</v-icon>
                    </v-btn>
                    <input
                        ref="attachmentInput"
                        type="file"
                        multiple
                        style="display: none;"
                        @change="onAttachmentFilesSelected"
                    />
                </div>
            </v-card-text>
            <v-divider class="ml-4 mr-5" />
            <v-card-text style="height: 84px;">
                <v-textarea
                    v-model="optionalMessage"
                    label="Message (Optional)"
                    rows="1"
                    hide-details
                ></v-textarea>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    v-if="alert.status === 'pending'"
                    color="error"
                    rounded
                    @click="handleDeleteAlert"
                    class="mr-2"
                >
                    DELETE
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="alertType === 'remove'"
                    color="error"
                    rounded
                    @click="handleReject"
                    class="mr-2"
                >
                    REJECT
                </v-btn>
                <v-btn
                    color="primary"
                    rounded
                    :loading="loading"
                    :disabled="loading"
                    @click="handleSubmit"
                >
                    {{ alertType === 'remove' ? 'ACCEPT' : 'SUBMIT CHANGES' }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog
            :retain-focus="false"
            v-model="itemFormDialog"
            persistent
            max-width="900px"
        >
            <ItemForm
                v-if="itemFormDialog"
                :workOrder="workOrder"
                :partNumbers="partNumbers"
                :item="currentNewItem"
                :createForm="true"
                :settingProcesses="settingProcesses"
                :settingFinish="settingFinish"
                :OCs="OCs"
                :positions="positions"
                :alertMode="true"
                @addItem="handleNewItemAdded"
                @close="closeAddItemDialog"
            />
        </v-dialog>

        <v-dialog v-model="fileUploadDialog" max-width="600px" persistent>
            <v-card>
                <v-card-title>
                    <v-btn
                        small
                        icon
                        @click="fileUploadDialog = false"
                        color="primary"
                        class="ml-n3 mr-2"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <h2 class="font-weight-regular">Upload Files</h2>
                </v-card-title>
                <v-divider class="ml-4 mr-5" />
                <v-card-text>
                    <v-file-input
                        v-model="filesToUpload"
                        label="Select files"
                        multiple
                        prepend-icon="mdi-paperclip"
                        show-size
                    >
                    </v-file-input>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary" rounded @click="confirmFileUpload">
                        UPLOAD
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="noChangesDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title>
                    <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
                    <span>No Changes Detected</span>
                </v-card-title>
                <v-divider />
                <v-card-text class="pt-4">
                    <p>
                        No modifications have been made to this alert. Are you
                        sure you want to continue without making any changes?
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="noChangesDialog = false">
                        CANCEL
                    </v-btn>
                    <v-btn color="primary" rounded @click="confirmNoChanges">
                        CONTINUE
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="deleteAlertDialog" max-width="450px" persistent>
            <v-card>
                <v-card-title class="text-h6">Delete alert</v-card-title>
                <v-card-text>
                    Are you sure you want to delete this alert?
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="deleteAlertDialog = false">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="error"
                        :loading="loading"
                        @click="confirmDeleteAlert"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import ItemForm from './ItemForm.vue'
import AssemblyForm from './AssemblyForm.vue'
import { saveFile } from '@/services/storage/saveFile.js'
import { deleteFile } from '@/services/storage/deleteFile.js'
import { openFile } from '@/services/storage/openFile.js'
import { objDiff } from '@/helpers/objDiff.js'
import _ from 'lodash'
import { MAX_WORK_ORDER_ITEMS } from '@/constants/workOrderLimits'

export default {
    name: 'AlertChangesDialog',
    components: {
        ItemForm,
        AssemblyForm,
    },
    props: {
        alert: {
            type: Object,
            required: true,
        },
        workOrder: {
            type: Object,
            required: true,
        },
        value: {
            type: Boolean,
            default: false,
        },
        OCs: {
            type: Array,
            required: true,
        },
        positions: {
            type: Object,
            required: true,
        },
        settingProcesses: {
            type: Object,
            required: true,
        },
        settingFinish: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        MAX_WORK_ORDER_ITEMS,
        loading: false,
        partNumbers: [],
        addedItems: [],
        editItems: {},
        currentEditTab: 0,
        currentAssemblyTab: 0,
        assemblyTabParents: {},
        assemblyCatalogItems: [],
        proposedChanges: {},
        optionalMessage: '',
        noChangesDialog: false,
        deleteAlertDialog: false,
        pendingSubmit: null,
        itemFormDialog: false,
        currentNewItem: {},
        currentNewItemIndex: null,
        fileUploadDialog: false,
        filesToUpload: [],
        currentFileUploadPartNumberId: null,
        addedItemsHeaders: [
            { text: 'Part Number', value: 'partNumber' },
            { text: 'Description', value: 'description' },
            { text: 'Quantity', value: 'quantity' },
            { text: 'Files', value: 'files', sortable: false },
            { text: 'Actions', value: 'actions', sortable: false },
        ],
        fileHeaders: [
            { text: 'File Name', value: 'name' },
            { text: 'Size', value: 'size' },
            { text: 'Actions', value: 'actions', sortable: false },
        ],
        partNumbersProductionData: {},
        editableAttachments: [],
        initialAttachmentNames: [],
        // True if user removed or uploaded attachments (same filename list can still mean new blob)
        attachmentsEdited: false,
        alertEditSkipProcessKeys: ['processes', 'dataProcesses', 'files'],
    }),
    computed: {
        dialog: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        alertType() {
            return this.alert?.alertType
        },
        alertTypeLabel() {
            const labels = {
                add: 'Add Items',
                edit: 'Edit Items',
                remove: 'Remove Items',
                editAssembly: 'Edit Assembly Items',
            }
            return labels[this.alertType] || ''
        },
        selectedPartNumbers() {
            return this.alert?.selectedPartNumbers || []
        },
        selectedPartNumbersSorted() {
            return [...this.selectedPartNumbers].sort((a, b) => {
                const last4DigitsA = parseInt(
                    String(a.partNumber).slice(-4),
                    10
                )
                const last4DigitsB = parseInt(
                    String(b.partNumber).slice(-4),
                    10
                )
                return last4DigitsA - last4DigitsB
            })
        },
        companyId() {
            const company = JSON.parse(localStorage.getItem('company') || '{}')
            return company.id || company
        },
        isMajorModification() {
            return this.alert?.classification === 'major modification'
        },
    },
    async mounted() {
        try {
            this.loading = true
            await this.loadData()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    watch: {
        value(newVal) {
            if (newVal) {
                this.loadData()
            }
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        handleDeleteAlert() {
            this.deleteAlertDialog = true
        },
        async confirmDeleteAlert() {
            try {
                this.loading = true
                await API.deleteAlert(this.alert.workOrderId, this.alert.id)
                this.deleteAlertDialog = false
                this.$emit('input', false)
                this.$emit('reload')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        partNumberLineHasProduction(partNumberKey) {
            const part = this.workOrder.partNumbers?.[partNumberKey]
            return Array.isArray(part?.production) && part.production.length > 0
        },
        updateProcesses(partNumberId, processes) {
            if (!this.editItems[partNumberId]) return
            this.$set(
                this.editItems[partNumberId].current,
                'processes',
                processes || []
            )
        },
        processIds(processes) {
            return this.normalizeProcessIds(processes)
        },
        normalizeProcessIds(processes) {
            if (!processes) return []
            if (Array.isArray(processes)) {
                return processes
                    .map(p =>
                        p != null && typeof p === 'object' && p.id != null
                            ? p.id
                            : p
                    )
                    .filter(p => p != null && p !== '')
                    .map(String)
                    .sort()
            }
            if (typeof processes === 'object') {
                return Object.keys(processes)
                    .map(String)
                    .sort()
            }
            return []
        },
        processesChangedBetween(originalItem, editedItem) {
            return !_.isEqual(
                this.normalizeProcessIds(editedItem?.processes),
                this.normalizeProcessIds(originalItem?.processes)
            )
        },
        itemDiffExcludingProcesses(originalItem, editedItem) {
            return objDiff(
                _.omit(originalItem, this.alertEditSkipProcessKeys),
                _.omit(editedItem, this.alertEditSkipProcessKeys)
            )
        },
        async loadData() {
            try {
                this.loading = true
                const companyId = JSON.parse(localStorage.getItem('company'))
                const projectId = this.workOrder?.projectId

                if (!projectId) {
                    throw new Error('Work order project ID not found')
                }

                this.assemblyTabParents = {}
                this.assemblyCatalogItems = []
                this.currentAssemblyTab = 0

                if (this.alertType === 'add') {
                    this.partNumbers = await API.getPartNumbers(
                        projectId,
                        null,
                        companyId
                    )
                } else if (this.alertType === 'edit') {
                    this.partNumbers = []
                    await this.loadEditItems()
                } else if (this.alertType === 'editAssembly') {
                    await this.loadAssemblyData(projectId, companyId)
                }
                this.editableAttachments = [
                    ...(this.workOrder.attachments || []),
                ]
                this.initialAttachmentNames = [
                    ...(this.workOrder.attachments || []),
                ]
                this.attachmentsEdited = false
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async loadEditItems() {
            this.editItems = {}
            for (const pn of this.selectedPartNumbers) {
                const item = this.workOrder.partNumbers?.[pn.id]
                if (item) {
                    const itemWithPartNumber = {
                        ...item,
                        partNumber: pn.partNumber,
                        partNumberId: pn.id,
                    }

                    const normalizedCurrent = _.cloneDeep(itemWithPartNumber)
                    const normalizedOriginal = _.cloneDeep(itemWithPartNumber)

                    this.editItems[pn.id] = {
                        current: normalizedCurrent,
                        original: normalizedOriginal,
                        files: _.cloneDeep(item.files || []),
                        partNumberCode: pn.partNumber,
                    }
                }
            }
        },
        async loadAssemblyData(projectId, companyId) {
            this.assemblyTabParents = {}
            this.assemblyCatalogItems = []
            if (!this.selectedPartNumbers.length) return

            const [list, productionData] = await Promise.all([
                API.getPartNumbers(projectId, null, companyId),
                API.getPartNumbersProductionData(projectId, companyId),
            ])
            this.partNumbers = list
            this.partNumbersProductionData = productionData || {}
            list.forEach(partNumber => {
                if (this.partNumbersProductionData[partNumber.id]) {
                    partNumber.available = this.partNumbersProductionData[
                        partNumber.id
                    ].available
                }
            })
            this.assemblyCatalogItems = list

            for (const pn of this.selectedPartNumbersSorted) {
                const raw = this.workOrder.partNumbers?.[pn.id]
                if (!raw) continue
                const parentForForm = _.cloneDeep(raw)
                if (!parentForForm.partNumberId) {
                    parentForForm.partNumberId = pn.id
                }
                if (
                    parentForForm.assemblyItems &&
                    !Array.isArray(parentForForm.assemblyItems)
                ) {
                    parentForForm.assemblyItems = Object.values(
                        parentForForm.assemblyItems
                    )
                } else if (!parentForForm.assemblyItems) {
                    parentForForm.assemblyItems = []
                }
                const initialAssemblyItems = _.cloneDeep(
                    parentForForm.assemblyItems || []
                )
                this.$set(this.assemblyTabParents, pn.id, {
                    parentForForm,
                    initialAssemblyItems,
                    originalFiles: _.cloneDeep(raw.files || []),
                })
            }
        },
        assemblyFormRef(partNumberId) {
            const r = this.$refs[`assemblyForm-${partNumberId}`]
            return Array.isArray(r) ? r[0] : r
        },
        computeAssemblyChanges(assemblyCtx, formComp) {
            const originalItems = assemblyCtx?.initialAssemblyItems || []
            if (!formComp || !Array.isArray(formComp.items)) {
                return {
                    itemsToAdd: [],
                    itemsToRemove: [],
                    itemsToUpdate: [],
                }
            }
            const updatedItems = (formComp.items || []).filter(
                i => i.qtyPerAssembly && i.qtyPerAssembly > 0
            )

            const itemsToAdd = []
            const itemsToRemove = []
            const itemsToUpdate = []

            updatedItems.forEach(updItem => {
                const origItem = originalItems.find(
                    item =>
                        item.partNumber === updItem.code ||
                        item.partNumberId === updItem.id
                )
                if (!origItem) {
                    itemsToAdd.push({
                        partNumber: updItem.code,
                        partNumberId: updItem.id,
                        qtyPerAssembly: Number(updItem.qtyPerAssembly),
                    })
                } else if (
                    origItem.qtyPerAssembly !== Number(updItem.qtyPerAssembly)
                ) {
                    itemsToUpdate.push({
                        partNumber: updItem.code,
                        partNumberId: updItem.id,
                        qtyPerAssembly: Number(updItem.qtyPerAssembly),
                        oldQtyPerAssembly: origItem.qtyPerAssembly,
                    })
                }
            })

            originalItems.forEach(origItem => {
                const found = updatedItems.find(
                    updItem =>
                        updItem.code === origItem.partNumber ||
                        updItem.id === origItem.partNumberId
                )
                if (!found) {
                    itemsToRemove.push({
                        partNumber: origItem.partNumber,
                        partNumberId: origItem.partNumberId,
                    })
                }
            })

            return { itemsToAdd, itemsToRemove, itemsToUpdate }
        },
        openAddItemDialog() {
            this.currentNewItem = {}
            this.currentNewItemIndex = null
            this.itemFormDialog = true
        },
        editAddedItem(item) {
            const index = this.addedItems.findIndex(
                i => i.tempId === item.tempId
            )
            const copy = { ...item }
            const code = String(
                typeof item.partNumber === 'object'
                    ? item.partNumber?.code || ''
                    : item.partNumber || ''
            ).trim()
            const match = (this.partNumbers || []).find(pn => {
                if (item.partNumberId && pn.id === item.partNumberId) {
                    return true
                }
                return code && String(pn.code || '').trim() === code
            })
            if (match) {
                copy.partNumber = match
            }
            this.currentNewItem = copy
            this.currentNewItemIndex = index
            this.itemFormDialog = true
        },
        removeAddedItem(item) {
            const index = this.addedItems.findIndex(
                i => i.tempId === item.tempId
            )
            if (index > -1) {
                this.addedItems.splice(index, 1)
            }
        },
        handleNewItemAdded(item) {
            const serializedItem = this.serializeItem(item)
            serializedItem.tempId = Date.now() + Math.random()

            if (this.currentNewItemIndex !== null) {
                this.addedItems.splice(
                    this.currentNewItemIndex,
                    1,
                    serializedItem
                )
            } else {
                this.addedItems.push(serializedItem)
            }

            this.closeAddItemDialog()
        },
        closeAddItemDialog() {
            this.itemFormDialog = false
            this.currentNewItem = {}
            this.currentNewItemIndex = null
        },
        syncEditItemFiles(partNumberId, files) {
            const editItem = this.editItems[partNumberId]
            if (!editItem) return
            const nextFiles = Array.isArray(files) ? [...files] : []
            this.$set(editItem, 'files', nextFiles)
            this.$set(editItem.current, 'files', nextFiles)
        },
        handleItemUpdated(partNumberId, item) {
            const editItem = this.editItems[partNumberId]
            if (!editItem) return
            if (editItem.current !== item) {
                this.$set(editItem, 'current', item)
            }
            this.syncEditItemFiles(
                partNumberId,
                Array.isArray(item.files) ? item.files : []
            )
        },
        handleReject() {
            this.proposedChanges = {
                type: 'remove',
                rejected: true,
            }
            this.submitChanges()
        },
        openFileUpload(partNumberId) {
            this.currentFileUploadPartNumberId = partNumberId
            this.filesToUpload = []
            this.fileUploadDialog = true
        },
        confirmFileUpload() {
            const partNumberId = this.currentFileUploadPartNumberId
            if (
                this.filesToUpload &&
                this.filesToUpload.length > 0 &&
                this.editItems[partNumberId]
            ) {
                const currentList =
                    this.editItems[partNumberId].current.files ||
                    (this.editItems[partNumberId].current.files = [])
                this.filesToUpload.forEach(file => {
                    const entry = {
                        id: Date.now() + Math.random(),
                        name: file.name,
                        size: file.size,
                        file,
                    }
                    this.editItems[partNumberId].files.push(entry)
                    currentList.push(entry)
                })
            }
            this.fileUploadDialog = false
            this.filesToUpload = []
            this.currentFileUploadPartNumberId = null
        },
        handleFileUpload(partNumberId, event) {
            const files = Array.from(event.target.files)
            if (!this.editItems[partNumberId]) return
            const list = this.editItems[partNumberId].files
            const currentList =
                this.editItems[partNumberId].current.files ||
                (this.editItems[partNumberId].current.files = [])
            files.forEach(file => {
                const entry = {
                    id: Date.now() + Math.random(),
                    name: file.name,
                    size: file.size,
                    file,
                }
                list.push(entry)
                currentList.push(entry)
            })
        },
        attachmentsPath() {
            return `${this.companyId}/workOrders/${this.workOrder.id}/attachments`
        },
        attachmentUploadPath() {
            if (this.isMajorModification) {
                return `${this.companyId}/alerts/${this.alert.id}/attachments`
            }
            return this.attachmentsPath()
        },
        attachmentOpenPath(fileName) {
            if (
                this.isMajorModification &&
                !this.initialAttachmentNames.includes(fileName)
            ) {
                return `${this.companyId}/alerts/${this.alert.id}/attachments`
            }
            return this.attachmentsPath()
        },
        async openAttachment(fileName) {
            try {
                await openFile(fileName, this.attachmentOpenPath(fileName))
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message || 'Error opening file',
                })
            }
        },
        removeAttachment(index) {
            this.editableAttachments.splice(index, 1)
            this.attachmentsEdited = true
        },
        async onAttachmentFilesSelected(event) {
            const files = Array.from(event.target.files || [])
            for (const file of files) {
                try {
                    await saveFile(file, this.attachmentUploadPath(), file.name)
                    this.editableAttachments.push(file.name)
                    this.attachmentsEdited = true
                } catch (error) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: error.message || 'Error uploading file',
                    })
                }
            }
            event.target.value = ''
        },
        async submitChanges() {
            this.loading = true
            try {
                await API.makeAlertChanges(this.alert.workOrderId, {
                    alertId: this.alert.id,
                    proposedChanges: this.proposedChanges,
                })
                this.$emit('input', false)
                this.$emit('reload')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
                throw error
            } finally {
                this.loading = false
            }
        },
        hasChanges() {
            const attachmentsChanged =
                this.attachmentsEdited ||
                JSON.stringify(this.editableAttachments || []) !==
                    JSON.stringify(this.workOrder.attachments || [])
            if (attachmentsChanged) return true
            if (this.alertType === 'remove') {
                return true
            }
            if (this.alertType === 'add') {
                return this.addedItems.length > 0
            }
            if (this.alertType === 'editAssembly') {
                const ids = Object.keys(this.assemblyTabParents)
                if (!ids.length) return false
                for (const partNumberId of ids) {
                    const ctx = this.assemblyTabParents[partNumberId]
                    const formComp = this.assemblyFormRef(partNumberId)
                    if (formComp && formComp.items) {
                        const delta = this.computeAssemblyChanges(ctx, formComp)
                        if (
                            (delta.itemsToAdd || []).length +
                                (delta.itemsToRemove || []).length +
                                (delta.itemsToUpdate || []).length >
                            0
                        ) {
                            return true
                        }
                    }
                    const currentFiles = ctx.parentForForm?.files || []
                    const originalFiles = ctx.originalFiles || []
                    if (this.computeFilesChanged(currentFiles, originalFiles)) {
                        return true
                    }
                }
                return false
            }
            if (this.alertType === 'edit') {
                for (const partNumberId of Object.keys(this.editItems)) {
                    const editItem = this.editItems[partNumberId]

                    let editedItem = _.cloneDeep(editItem.current)
                    let originalItem = editItem.original
                    if (typeof editedItem.finishAndUCCode === 'object') {
                        editedItem.finishAndUCCode =
                            editedItem.finishAndUCCode.id
                    }

                    const itemDiff = this.itemDiffExcludingProcesses(
                        originalItem,
                        editedItem
                    )
                    const processesChanged = this.processesChangedBetween(
                        originalItem,
                        editedItem
                    )

                    if (Object.keys(itemDiff).length > 0 || processesChanged) {
                        return true
                    }

                    const filesChanged = this.computeFilesChanged(
                        editedItem.files,
                        originalItem.files
                    )
                    if (filesChanged) {
                        return true
                    }
                }
                return false
            }
            return false
        },
        async handleSubmit() {
            try {
                this.loading = true
                if (!this.hasChanges()) {
                    this.pendingSubmit = () => this.processSubmit()
                    this.noChangesDialog = true
                    return
                }
                await this.processSubmit()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async confirmNoChanges() {
            this.noChangesDialog = false
            if (this.pendingSubmit) {
                await this.pendingSubmit()
                this.pendingSubmit = null
            }
        },
        async rollbackUploadedFiles(uploadedRefs) {
            for (const ref of uploadedRefs || []) {
                try {
                    if (ref.path && ref.fileName) {
                        await deleteFile(ref.fileName, ref.path)
                    }
                } catch (error) {
                    console.error('Failed to rollback uploaded file:', error)
                }
            }
        },
        normalizeFileList(files) {
            return (files || [])
                .map(f => (typeof f === 'string' ? f : f.name || f.id))
                .filter(Boolean)
                .sort()
        },
        // Detects a real file change, including the case where a user replaces
        // an existing file with a new one that keeps the same required file name
        // (in which case comparing normalized names alone would miss the change).
        // Must be called BEFORE uploadFilesForEditedItems/uploadFilesForAssemblyParents
        // run, since those calls replace pending upload entries (with `.file`) with
        // plain "uploaded" descriptors, losing the signal that a new blob was staged.
        hasPendingFileUpload(files) {
            return (files || []).some(f => f && typeof f === 'object' && f.file)
        },
        computeFilesChanged(currentFiles, originalFiles) {
            if (this.hasPendingFileUpload(currentFiles)) return true
            return (
                JSON.stringify(this.normalizeFileList(currentFiles)) !==
                JSON.stringify(this.normalizeFileList(originalFiles))
            )
        },
        mapFilesForPayload(filesList, basePath) {
            return (filesList || []).map(f =>
                typeof f === 'string'
                    ? {
                          id: f,
                          name: f,
                          url: `${basePath}/${f}`,
                      }
                    : {
                          id: f.id || f.name,
                          name: f.name,
                          size: f.size,
                          url: f.url || `${basePath}/${f.name}`,
                      }
            )
        },
        async uploadFilesForAssemblyParents() {
            const basePath = this.isMajorModification
                ? `${this.companyId}/alerts/${this.alert.id}/items`
                : `${this.companyId}/workOrders/${this.workOrder.id}/items`
            const workOrderItemsPath = `${this.companyId}/workOrders/${this.workOrder.id}/items`
            for (const partNumberId of Object.keys(this.assemblyTabParents)) {
                const ctx = this.assemblyTabParents[partNumberId]
                const parentLine = ctx?.parentForForm
                if (!parentLine) continue
                const filesList = parentLine.files || []
                if (filesList.length === 0) continue
                const uploadedFiles = []
                for (const fileObj of filesList) {
                    if (typeof fileObj === 'string') {
                        // Pre-existing file: it already lives in the work order's
                        // folder, it was never uploaded to the alert staging path.
                        uploadedFiles.push({
                            id: fileObj,
                            name: fileObj,
                            url: `${workOrderItemsPath}/${fileObj}`,
                            uploaded: true,
                        })
                    } else if (fileObj.file && !fileObj.uploaded) {
                        const fileName = fileObj.name
                        await saveFile(fileObj.file, basePath, fileName)
                        uploadedFiles.push({
                            id: fileName,
                            name: fileObj.name,
                            url: `${basePath}/${fileName}`,
                            uploaded: true,
                        })
                    } else if (fileObj.uploaded) {
                        uploadedFiles.push(fileObj)
                    }
                }
                this.$set(parentLine, 'files', uploadedFiles)
            }
        },
        async processSubmit() {
            try {
                if (this.alertType === 'add') {
                    const currentPartNumbersCount = Object.keys(
                        this.workOrder.partNumbers || {}
                    ).length
                    const newItemsCount = this.addedItems.length

                    if (
                        currentPartNumbersCount + newItemsCount >
                        MAX_WORK_ORDER_ITEMS
                    ) {
                        throw {
                            message: `Cannot add ${newItemsCount} item(s). Work orders are limited to ${MAX_WORK_ORDER_ITEMS} part numbers. Current: ${currentPartNumbersCount}`,
                        }
                    }

                    let uploadedRefs = []
                    try {
                        const items = this.addedItems.map(item =>
                            this.serializeItem(item)
                        )
                        const missingPartNumber = items.some(
                            item =>
                                !String(item.partNumber || '').trim() ||
                                !String(item.partNumberId || '').trim()
                        )
                        if (missingPartNumber) {
                            throw {
                                message:
                                    'Each added item must include a part number',
                            }
                        }

                        uploadedRefs = await this.uploadFilesForAddedItems()

                        this.proposedChanges = {
                            type: 'add',
                            items,
                            message: this.optionalMessage || undefined,
                            attachments: [...this.editableAttachments],
                        }
                        await this.submitChanges()
                    } catch (error) {
                        await this.rollbackUploadedFiles(uploadedRefs)
                        throw error
                    }
                } else if (this.alertType === 'remove') {
                    this.proposedChanges = {
                        type: 'remove',
                        accepted: true,
                        message: this.optionalMessage || undefined,
                        attachments: [...this.editableAttachments],
                    }
                    await this.submitChanges()
                } else if (this.alertType === 'editAssembly') {
                    const parentIds = Object.keys(this.assemblyTabParents)
                    // Compute filesChanged BEFORE uploading: once files are
                    // uploaded, pending entries (with `.file`) are replaced by
                    // plain "uploaded" descriptors, which would make a same-name
                    // file replacement indistinguishable from an untouched file.
                    const filesChangedByPartNumber = {}
                    parentIds.forEach(partNumberId => {
                        const ctx = this.assemblyTabParents[partNumberId]
                        filesChangedByPartNumber[
                            partNumberId
                        ] = this.computeFilesChanged(
                            ctx?.parentForForm?.files,
                            ctx?.originalFiles
                        )
                    })

                    await this.uploadFilesForAssemblyParents()
                    const assemblyEdits = []
                    const basePath = this.isMajorModification
                        ? `${this.companyId}/alerts/${this.alert.id}/items`
                        : `${this.companyId}/workOrders/${this.workOrder.id}/items`
                    for (const partNumberId of parentIds) {
                        const formComp = this.assemblyFormRef(partNumberId)
                        const assemblyCtx = this.assemblyTabParents[
                            partNumberId
                        ]
                        const filesChanged =
                            filesChangedByPartNumber[partNumberId]
                        if (!formComp && !filesChanged) {
                            continue
                        }
                        const assemblyChanges = this.computeAssemblyChanges(
                            assemblyCtx,
                            formComp
                        )
                        const hasAsmDelta =
                            (assemblyChanges.itemsToAdd || []).length +
                                (assemblyChanges.itemsToRemove || []).length +
                                (assemblyChanges.itemsToUpdate || []).length >
                            0

                        const parentLine = assemblyCtx.parentForForm

                        if (this.partNumberLineHasProduction(partNumberId)) {
                            if (hasAsmDelta) {
                                throw {
                                    message:
                                        'Assembly line items cannot be changed for a part number that has production recorded.',
                                }
                            }
                            if (!filesChanged) {
                                continue
                            }
                        }

                        const block = { partNumberId }
                        if (hasAsmDelta) {
                            block.assemblyChanges = assemblyChanges
                        }
                        if (filesChanged) {
                            const filesList = parentLine.files || []
                            block.files = filesList.map(f =>
                                typeof f === 'string'
                                    ? {
                                          id: f,
                                          name: f,
                                          url: `${basePath}/${f}`,
                                      }
                                    : {
                                          id: f.id,
                                          name: f.name,
                                          size: f.size,
                                          url: f.url || `${basePath}/${f.name}`,
                                      }
                            )
                        }
                        if (block.assemblyChanges || block.files) {
                            assemblyEdits.push(block)
                        }
                    }

                    this.proposedChanges = {
                        type: 'editAssembly',
                        assemblyEdits,
                        message: this.optionalMessage || undefined,
                        attachments: [...this.editableAttachments],
                    }
                    await this.submitChanges()
                } else if (this.alertType === 'edit') {
                    let uploadedRefs = []
                    try {
                        // Compute filesChanged BEFORE uploading: once files are
                        // uploaded, pending entries (with `.file`) are replaced by
                        // plain "uploaded" descriptors, which would make a same-name
                        // file replacement indistinguishable from an untouched file.
                        const filesChangedByPartNumber = {}
                        Object.keys(this.editItems).forEach(partNumberId => {
                            const editItem = this.editItems[partNumberId]
                            filesChangedByPartNumber[
                                partNumberId
                            ] = this.computeFilesChanged(
                                editItem.current.files,
                                editItem.original.files
                            )
                        })

                        uploadedRefs = await this.uploadFilesForEditedItems()

                        const edits = []
                        for (const partNumberId of Object.keys(
                            this.editItems
                        )) {
                            const editItem = this.editItems[partNumberId]

                            let editedItem = _.cloneDeep(editItem.current)
                            let originalItem = editItem.original
                            if (
                                typeof editedItem.finishAndUCCode === 'object'
                            ) {
                                editedItem.finishAndUCCode =
                                    editedItem.finishAndUCCode.id
                            }

                            const itemDiff = this.itemDiffExcludingProcesses(
                                originalItem,
                                editedItem
                            )
                            const processesChanged = this.processesChangedBetween(
                                originalItem,
                                editedItem
                            )
                            const filesChanged =
                                filesChangedByPartNumber[partNumberId]

                            if (
                                Object.keys(itemDiff).length > 0 ||
                                processesChanged ||
                                filesChanged
                            ) {
                                const serializedItem = this.serializeItem(
                                    editedItem
                                )
                                const partNumberCode =
                                    editItem.partNumberCode ||
                                    editItem.current.partNumber ||
                                    serializedItem.partNumber
                                const basePath = this.isMajorModification
                                    ? `${this.companyId}/alerts/${this.alert.id}/items`
                                    : `${this.companyId}/workOrders/${this.workOrder.id}/items`
                                const editPayload = {
                                    partNumberId: partNumberId,
                                    partNumberCode: partNumberCode,
                                    item: serializedItem,
                                    itemDiff,
                                    processesChanged,
                                }
                                if (filesChanged) {
                                    const filesList =
                                        editItem.current.files ||
                                        editItem.files ||
                                        []
                                    editPayload.files = this.mapFilesForPayload(
                                        filesList,
                                        basePath
                                    )
                                }
                                if (processesChanged) {
                                    editPayload.processIds = this.normalizeProcessIds(
                                        editedItem.processes
                                    )
                                }
                                edits.push(editPayload)
                            }
                        }

                        this.proposedChanges = {
                            type: 'edit',
                            edits: edits,
                            message: this.optionalMessage || undefined,
                            attachments: [...this.editableAttachments],
                        }
                        await this.submitChanges()
                    } catch (error) {
                        await this.rollbackUploadedFiles(uploadedRefs)
                        throw error
                    }
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        async uploadFilesForAddedItems() {
            const uploadedRefs = []
            for (const item of this.addedItems) {
                if (item.files && item.files.length > 0) {
                    const uploadedFiles = []
                    for (const fileObj of item.files) {
                        if (fileObj.file && !fileObj.uploaded) {
                            const storagePath = this.isMajorModification
                                ? `${this.companyId}/alerts/${this.alert.id}/items`
                                : `${this.companyId}/workOrders/${this.workOrder.id}/items`

                            const fileName = fileObj.name
                            await saveFile(fileObj.file, storagePath, fileName)
                            uploadedRefs.push({ path: storagePath, fileName })

                            uploadedFiles.push({
                                id: fileName,
                                name: fileObj.name,
                                url: `${storagePath}/${fileName}`,
                                uploaded: true,
                            })
                        } else if (fileObj.uploaded) {
                            uploadedFiles.push(fileObj)
                        }
                    }
                    item.files = uploadedFiles
                }
            }
            return uploadedRefs
        },
        async uploadFilesForEditedItems() {
            const uploadedRefs = []
            const basePath = this.isMajorModification
                ? `${this.companyId}/alerts/${this.alert.id}/items`
                : `${this.companyId}/workOrders/${this.workOrder.id}/items`
            const workOrderItemsPath = `${this.companyId}/workOrders/${this.workOrder.id}/items`
            for (const partNumberId of Object.keys(this.editItems)) {
                const editItem = this.editItems[partNumberId]
                const filesList = editItem.current.files || editItem.files || []
                if (filesList.length > 0) {
                    const uploadedFiles = []
                    for (const fileObj of filesList) {
                        if (typeof fileObj === 'string') {
                            // Pre-existing file: it already lives in the work order's
                            // folder, it was never uploaded to the alert staging path.
                            uploadedFiles.push({
                                id: fileObj,
                                name: fileObj,
                                url: `${workOrderItemsPath}/${fileObj}`,
                                uploaded: true,
                            })
                        } else if (fileObj.file && !fileObj.uploaded) {
                            const fileName = fileObj.name
                            await saveFile(fileObj.file, basePath, fileName)
                            uploadedRefs.push({ path: basePath, fileName })
                            uploadedFiles.push({
                                id: fileName,
                                name: fileObj.name,
                                url: `${basePath}/${fileName}`,
                                uploaded: true,
                            })
                        } else if (fileObj.uploaded) {
                            uploadedFiles.push(fileObj)
                        }
                    }
                    editItem.files = uploadedFiles
                    editItem.current.files = uploadedFiles
                }
            }
            return uploadedRefs
        },
        serializeItem(item) {
            const serialized = {
                description: item.description || '',
                quantity: item.quantity || 0,
                mass: item.mass || 0,
                paintingArea: item.paintingArea || 0,
                pieceLength: item.pieceLength ?? null,
                pieceWidth: item.pieceWidth ?? null,
                notes: item.notes || '',
                oc: item.oc,
                costPosition: item.costPosition || '',
            }

            if (item.partNumber && typeof item.partNumber === 'object') {
                serialized.partNumber =
                    item.partNumber.code || item.partNumber.id
                serialized.partNumberId =
                    item.partNumberId || item.partNumber.id
            } else if (item.partNumber) {
                serialized.partNumber = item.partNumber
                serialized.partNumberId = item.partNumberId || item.id
            }

            if (item.finishAndUCCode) {
                if (typeof item.finishAndUCCode === 'object') {
                    serialized.dataFinishAndUCCode = {
                        id: item.finishAndUCCode.id,
                        name: item.finishAndUCCode.name,
                    }
                    serialized.finishAndUCCode = item.finishAndUCCode.id
                } else {
                    serialized.finishAndUCCode = item.finishAndUCCode
                    if (item.dataFinishAndUCCode) {
                        serialized.dataFinishAndUCCode =
                            item.dataFinishAndUCCode
                    }
                }
            }

            if (item.processes) {
                if (Array.isArray(item.processes)) {
                    serialized.dataProcesses = item.processes.map(p =>
                        typeof p === 'object' ? { id: p.id, name: p.name } : p
                    )
                    serialized.processes = item.processes.map(p =>
                        typeof p === 'object' ? p.id : p
                    )
                } else {
                    serialized.processes = item.processes
                    if (item.dataProcesses) {
                        serialized.dataProcesses = item.dataProcesses
                    }
                }
            }

            if (item.oc && typeof item.oc === 'object') {
                serialized.oc = item.oc.id
            }

            if (item.settingFinishId)
                serialized.settingFinishId = item.settingFinishId
            if (item.settingProcessesId)
                serialized.settingProcessesId = item.settingProcessesId

            if (item.files && item.files.length > 0) {
                serialized.files = item.files
            }

            return serialized
        },
        close() {
            this.$emit('input', false)
        },
    },
}
</script>

<style scoped></style>
